// src/ui/auth/AddProfilePage.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import authService from '../../services/auth/authService';

const MAX_NAME_LENGTH = 21;

export const AddProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const state = location.state || {};
  const uid = state.uid || '';
  const phoneNumber = state.phoneNumber || '';

  const [name, setName] = useState('');
  const [previewUsername, setPreviewUsername] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [pickedImage, setPickedImage] = useState(null);
  const [pickedImageFile, setPickedImageFile] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [syncContacts, setSyncContacts] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const nameInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Computed ──────────────────────────────────────────────────────────────

  const isValid = name.trim().length >= 2 && !isCheckingUsername;

  // ── Auto-generate username preview as user types ──────────────────────────

  useEffect(() => {
    if (!name.trim()) {
      setPreviewUsername('');
      return;
    }

    const timer = setTimeout(async () => {
      setIsCheckingUsername(true);
      try {
        const generated = await authService.generateUniqueUsername(name.trim());
        if (name.trim() === name) {
          setPreviewUsername(generated);
        }
      } catch (error) {
        console.error('Username generation error:', error);
      } finally {
        setIsCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [name]);

  // ── Focus name input on mount ─────────────────────────────────────────────

  useEffect(() => {
    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 300);
  }, []);

  // ── Image picker ──────────────────────────────────────────────────────────

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setPickedImage(event.target.result);
      setPickedImageFile(file);
    };
    reader.readAsDataURL(file);
  };

  // ── Upload image to Firebase Storage ─────────────────────────────────────

  const uploadImage = async () => {
    if (!pickedImageFile) return null;

    setIsUploadingImage(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `profile_images/${uid}.jpg`);
      await uploadBytes(storageRef, pickedImageFile);
      const url = await getDownloadURL(storageRef);
      return url;
    } catch (error) {
      console.error('Image upload error:', error);
      return null;
    } finally {
      setIsUploadingImage(false);
    }
  };

  // ── Save profile ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!isValid || isSaving) return;

    setIsSaving(true);
    setError(null);

    try {
      // Upload image if selected
      const imageUrl = await uploadImage();

      // Generate final unique username
      const finalUsername = await authService.generateUniqueUsername(name.trim());

      // Save user profile
      await authService.saveUserProfile({
        uid: uid,
        name: name.trim(),
        username: finalUsername,
        phone: phoneNumber,
        profileImageUrl: imageUrl,
        syncContacts: syncContacts,
      });

      // Add isVerified and isAdmin fields
      await updateDoc(doc(db, 'users', uid), {
        isVerified: false,
        isAdmin: false,
      });

      // Navigate to home
      navigate('/', { replace: true });
    } catch (error) {
      setError(error.message || 'Failed to save profile. Please try again.');
      setIsSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg px-6">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="max-w-md mx-auto">
        {/* Top Bar - Done button */}
        <div className="flex justify-end pt-4">
          <button
            onClick={handleSave}
            disabled={!isValid || isSaving}
            className={`text-sm font-bold transition-colors ${
              isValid && !isSaving
                ? 'text-filatom-primary hover:opacity-80'
                : 'text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <div className="w-4.5 h-4.5 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              'Done'
            )}
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="pb-8">
          {/* Profile Image Picker */}
          <div className="flex justify-center mt-4">
            <button
              onClick={handlePickImage}
              className="relative group"
            >
              <div
                className="w-26 h-26 rounded-full flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: '#23262C' }}
              >
                {pickedImage ? (
                  <img
                    src={pickedImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-13 h-13 text-white/40" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                )}
              </div>
              <div
                className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#FF8C08' }}
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </button>
          </div>

          <p className="text-center text-white/50 text-xs mt-2">
            Set profile photo (optional)
          </p>

          {/* Name Field */}
          <div className="mt-7">
            <div
              className="rounded-xl px-4 py-1"
              style={{ backgroundColor: '#23262C' }}
            >
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={MAX_NAME_LENGTH}
                placeholder="Your name"
                className="w-full bg-transparent text-white text-sm outline-none placeholder:text-white/40 py-2"
                style={{ border: 'none' }}
              />
              <div className="flex justify-end text-white/30 text-xs pb-1">
                {name.length}/{MAX_NAME_LENGTH}
              </div>
            </div>
          </div>

          {/* Username Preview */}
          {name.trim() && (
            <div className="flex items-center gap-1.5 mt-1.5">
              {isCheckingUsername ? (
                <div className="w-3 h-3 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              )}
              <span
                className="text-xs"
                style={{
                  color: isCheckingUsername ? 'rgba(255,255,255,0.4)' : '#FF8C08',
                }}
              >
                {isCheckingUsername ? 'Generating username…' : previewUsername}
              </span>
            </div>
          )}

          <p className="text-white/30 text-xs mt-1.5">
            Name must be at least 2 characters (max {MAX_NAME_LENGTH})
          </p>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Sync Contacts Toggle */}
          <div
            className="mt-6 rounded-xl flex items-center justify-between px-4 py-2"
            style={{ backgroundColor: '#23262C' }}
          >
            <span className="text-white text-sm">Sync contacts</span>
            <button
              onClick={() => setSyncContacts(!syncContacts)}
              className={`w-12 h-6 rounded-full transition-all relative ${
                syncContacts ? 'bg-filatom-primary' : 'bg-gray-600'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full bg-white transition-all absolute top-0.5 ${
                  syncContacts ? 'right-0.5' : 'left-0.5'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProfilePage;